/**
 * Où monter le routeur, à partir de l'URL d'entrée (#50, #37).
 *
 * Toutes les routes de l'app vivent sous `/o/:slug` via le basename react-router :
 * c'est ce qui préfixe automatiquement `navigate` et `Link`. Une URL nue est donc
 * réécrite vers l'org par défaut avant montage.
 *
 * Trois routes échappent à cette règle, et pas par commodité : `/signup`,
 * `/confirm-trial` et `/login` sont les URL **publiques** de la plateforme — les
 * deux CTA de la landing (essai, connexion) et le lien de vérification parti dans
 * une boîte mail. `/login` nu menait à l'org par défaut, un vrai client : c'est
 * désormais l'écran où le visiteur nomme son organisation (#346). La racine `/`
 * y mène aussi : c'est l'adresse qu'on tape quand on ne connaît que le domaine. Elles
 * doivent rester telles quelles dans la barre d'adresse : un prospect n'a pas à
 * découvrir notre découpage interne (`/o/default/…`), et une page atteignable par
 * deux chemins fausse la mesure d'audience (Studeva/infra#2).
 *
 * Fonction pure, épinglée dans `orgRouting.spec.ts` : c'est le seul endroit qui
 * décide quelle URL est plateforme et laquelle est scopée à une org.
 */

/**
 * Espace de l'opérateur plateforme — miroir de `SYSTEM_ORG_SLUG` côté API. Ne
 * correspond à AUCUNE ligne `Organization` : c'est l'unique point d'entrée des
 * comptes SuperAdmin, qui n'ont pas d'organisation.
 */
export const SYSTEM_ORG_SLUG = '_system'

/** Slug de repli quand l'URL n'en porte pas — miroir de `DEFAULT_ORG_ID` côté API. */
const DEFAULT_ORG_SLUG = 'default'

/**
 * Routes publiques d'acquisition, servies à la racine. Toute autre route nue
 * reste réécrite vers `/o/default/…`.
 */
export const PLATFORM_PUBLIC_PATHS = ['/signup', '/confirm-trial', '/login'] as const

export type Routing = {
  /** `platform` = servie à la racine ; `org` = sous un basename `/o/:slug`. */
  mode: 'platform' | 'org'
  /** Basename à passer à `createBrowserRouter`. */
  basename: string
  /**
   * Réécriture d'URL à effectuer avant montage, si l'URL d'entrée n'est ni
   * scopée ni plateforme. Absent sinon — une URL correcte ne rebondit jamais.
   */
  redirectTo?: string
}

/** Retire le `/` final (sauf sur la racine), pour que `/signup/` vaille `/signup`. */
function normalize(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
}

/** L'URL est-elle une route publique de la plateforme, servie hors basename d'org ? */
export function isPlatformPublicPath(pathname: string): boolean {
  return (PLATFORM_PUBLIC_PATHS as readonly string[]).includes(normalize(pathname))
}

/** Le slug d'une URL scopée (`/o/:slug/…`), absent d'une URL plateforme ou nue. */
function orgSlugOf(pathname: string): string | undefined {
  return pathname.match(/^\/o\/([^/]+)/)?.[1]
}

/** Décide du montage du routeur pour une URL d'entrée donnée. */
export function resolveRouting(pathname: string, search = ''): Routing {
  const orgSlug = orgSlugOf(pathname)
  // Une URL scopée reste scopée, y compris sur `/o/<slug>/signup` : le garde-fou
  // qui la referme sur une org cliente (#216) vit dans l'écran, pas ici.
  if (orgSlug) return { mode: 'org', basename: `/o/${orgSlug}` }

  if (pathname === '/' || isPlatformPublicPath(pathname)) return { mode: 'platform', basename: '/' }

  return {
    mode: 'org',
    basename: `/o/${DEFAULT_ORG_SLUG}`,
    redirectTo: `/o/${DEFAULT_ORG_SLUG}${pathname}${search}`,
  }
}

/**
 * Où renvoyer un compte connecté égaré sous le slug d'une autre org (#241) : la
 * même page, sous le sien. Sans quoi il porterait l'habillage et la
 * configuration de l'org de l'URL, quand ses données restent celles de la sienne.
 *
 * `null` quand il n'y a pas à bouger : déjà chez lui, URL non scopée, ou slug
 * de son espace inconnu.
 */
export function homeOrgRedirect(url: URL, homeSlug: string | null): string | null {
  const orgSlug = orgSlugOf(url.pathname)
  if (!homeSlug || !orgSlug || orgSlug === homeSlug) return null

  const page = url.pathname.slice(`/o/${orgSlug}`.length)
  return `/o/${homeSlug}${page}${url.search}${url.hash}`
}

/** L'écran où le visiteur nomme son organisation, avec celle de l'URL qu'on n'a pas trouvée (#346). */
export function orgFinderPath(unknownSlug?: string): string {
  return unknownSlug ? `/login?org=${encodeURIComponent(unknownSlug)}` : '/login'
}

/**
 * Où renvoyer un visiteur dont l'URL nomme une organisation, une fois que l'API a
 * dit laquelle c'est (#346). Sans ce contrôle, un slug inconnu retombait sans
 * bruit sur l'habillage de l'org par défaut, un vrai client.
 *
 * - inconnue : sur l'écran qui demande l'organisation, avec l'erreur ;
 * - trouvée sous une autre graphie (`/o/Diego`) : la même page sous son slug,
 *   le seul que l'API sait relier à son habillage ;
 * - `null` : rien à faire, ou URL non scopée, ou espace plateforme (`_system`,
 *   qui n'est la ligne d'aucune organisation).
 */
export function knownOrgRedirect(url: URL, found: string | null): string | null {
  const orgSlug = orgSlugOf(url.pathname)
  if (!orgSlug || orgSlug === SYSTEM_ORG_SLUG) return null
  if (found === null) return orgFinderPath(orgSlug)
  return homeOrgRedirect(url, found)
}

/** Décision pour l'URL courante du navigateur. */
export function currentRouting(): Routing {
  return resolveRouting(window.location.pathname, window.location.search)
}
